import { format } from 'date-fns'
import { Button, ButtonLink, DetailIcon, Title } from '@/components'

import Image from 'next/image'

const myLoader = ({ src }) => {
  return `${process.env.NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT}/${src}`
}

interface ConfirmProps {
  data?: any
  handleBack?: () => void
  canGoBack?: boolean
  handleCreate: (data: any) => any
}

export const ConfirmRental = ({ data, handleBack, canGoBack = true, handleCreate }: ConfirmProps) => {
  const submitReview = async () => {
    const body = {
      listing: {
        listingType: data.listingType,
        unitNumber: data.unitNumber,
        streetNumber: data.streetNumber,
        streetName: data.streetName,
        city: data.city,
        country: data.country,
        region: data.region,
        neighbourhood: data.neighbourhood,
        postalzip: data.postalZip,
      },
      rental: {
        description: data.description,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        rentPrice: data.rentPrice,
        availability: data.availability,
        email: data.email,
        canEmail: Boolean(data.canEmail),
        phoneNumber: data.phoneNumber,
        canPhone: Boolean(data.canPhone),
        outdoorSpace: Boolean(data.outdoorSpace),
        controlledAccess: Boolean(data.controlledAccess),
        storageAvailable: Boolean(data.storageAvailable),
        balcony: Boolean(data.balcony),
      },
      details: {
        petsWelcome: Boolean(data.petsWelcome),
        petsWelcomeStory: data.petsWelcomeStory,
        landlordLanguages: Boolean(data.landlordLanguages),
        landlordLanguagesStory: data.landlordLanguagesStory,
        accessibility: Boolean(data.accessibility),
        accessibilityStory: data.accessibilityStory,
        dishwasherAvailable: Boolean(data.dishwasherAvailable),
        airConditioningIncluded: Boolean(data.airConditioningIncluded),
        covidFriendly: Boolean(data.covidFriendly),
        parkingLocation: data.parkingLocation,
        parkingAvgPrice: data.parkingAvgPrice,
        laundryLocation: data.laundryLocation,
        laundryIncluded: Boolean(data.laundryIncluded),
        laundryAvgPrice: data.laundryAvgPrice,
        dryerIncluded: Boolean(data.dryerIncluded),
        dryerAvgPrice: data.dryerAvgPrice,
        gasIncluded: Boolean(data.gasIncluded),
        gasAvgPrice: data.gasAvgPrice,
        hydroIncluded: Boolean(data.hydroIncluded),
        hydroAvgPrice: data.hydroAvgPrice,
        waterIncluded: Boolean(data.waterIncluded),
        waterAvgPrice: data.waterAvgPrice,
      },
      images: data.images.map((image) => ({
        filePath: image.filePath,
        fileName: image.name,
        fileType: image.fileType,
        width: image.width,
        height: image.height,
        fileId: image.fileId,
      })),
    }
    handleCreate(body)
  }

  return (
    <div className="px-5 py-8 sm:px-6">
      <div className="mb-6 pb-6 border-b dark:border-camel border-black">
        <Title as="h3" size="lg">
          Confirm your rental listing details
        </Title>
        <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
          This information will be displayed publicly so be careful what you share.
        </p>
      </div>

      <Title className="mb-2 text-zinc-500 dark:text-zinc-300 font-medium">
        Available: {format(new Date(data.availability), 'MMMM d, yyyy')}
      </Title>
      <div
        className="text-base text-zinc-600 dark:text-zinc-100 prose "
        dangerouslySetInnerHTML={{ __html: data.description }}
      />
      <ul className="mt-8 flex">
        {data.images.map((image, idx) => (
          <li key={image.name} className="relative">
            <div className="rounded-lg overflow-hidden shadow-subtle h-[100px]">
              <Image
                loader={myLoader}
                width="100"
                height="100"
                src={`tr:w-100,h-100${image.filePath}`}
                className="rounded-lg mb-0 leading-none"
              />
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <DetailIcon icon="gas" theme="bg-green-200" iconClass="stroke-green-800" name="gas">
            {data.gasIncluded || data.gasAvgPrice === 0 ? 'Gas Included' : `Gas approx $${data.gasAvgPrice} per month`}
          </DetailIcon>

          <DetailIcon icon="water" theme="bg-indigo-200" iconClass="stroke-indigo-800" name="water">
            {data.waterIncluded || data.waterAvgPrice === 0
              ? 'Water Included'
              : `Water approx $${data.waterAvgPrice} per month`}
          </DetailIcon>

          <DetailIcon icon="hydro" theme="bg-red-200" iconClass="stroke-red-800" name="hydro">
            {data.hydroIncluded || data.hydroAvgPrice === 0
              ? 'Electricity Included'
              : `Electricity approx $${data.hydroAvgPrice} per month`}
          </DetailIcon>

          <DetailIcon icon="dishwasher" theme="bg-green-200" iconClass="stroke-green-800" name="dishwasher">
            {data.dishwasherAvailable ? 'Dishwasher Included' : 'No dishwasher'}
          </DetailIcon>

          <DetailIcon icon="airconditioner" theme="bg-brand-50" iconClass="stroke-brand-800" name="airconditioner">
            {data.airConditioningIncluded ? 'Air Conditioning Included' : 'No air conditioning'}
          </DetailIcon>

          <DetailIcon icon="storage" theme="bg-amber-200" iconClass="stroke-amber-800" name="storage">
            {data.storageAvailable ? 'Storage available' : 'No storage'}
          </DetailIcon>

          <DetailIcon
            icon="controlled_access"
            theme="bg-orange-200"
            iconClass="stroke-orange-800"
            name="controlled access"
          >
            {data.controlledAccess ? 'Controlled Access available' : 'No Controlled Access'}
          </DetailIcon>

          <DetailIcon icon="outdoor_space" theme="bg-rose-200" iconClass="stroke-rose-800" name="outdoor space">
            {data.outdoorSpace ? 'Outdoor Space available' : 'No outdoor space'}
          </DetailIcon>

          <DetailIcon icon="balcony" theme="bg-emerald-200" iconClass="stroke-emerald-800" name="balcony">
            {data.balcony ? 'Balcony available' : 'No balcony'}
          </DetailIcon>

          <DetailIcon icon="parking" theme="bg-lime-200" iconClass="stroke-lime-800" name="parking">
            {data.parkingLocation === 'none'
              ? 'No parking available'
              : data.parkingLocation.charAt(0).toUpperCase() + data.parkingLocation.slice(1)}
            {data.parkingLocation !== 'none' && data.parkingAvgPrice
              ? `: approx $${data.parkingAvgPrice} per month`
              : null}
          </DetailIcon>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-6">
          <DetailIcon
            icon="laundry"
            theme="bg-lime-200"
            iconClass="stroke-lime-800"
            name="laundry"
            metrics={
              data.laundryLocation !== 'none' ? (
                <>
                  <p className="text-sm mt-0.5 prose text-zinc-500 dark:text-zinc-400">
                    {data.laundryAvgPrice
                      ? `Washing Machine $${data.laundryAvgPrice} per load`
                      : `Washing Machine included`}
                  </p>
                  <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">
                    {data.dryerAvgPrice ? `Dryer $${data.dryerAvgPrice} per load` : `Dryer included`}
                  </p>
                </>
              ) : null
            }
          >
            {data.laundryLocation === 'none' ? 'No laundry available' : `Laundry available in ${data.laundryLocation}`}
          </DetailIcon>

          <DetailIcon
            icon="pets"
            theme="bg-yellow-200"
            iconClass="stroke-yellow-800"
            name="pets"
            metrics={
              data.petsWelcomeStory ? (
                <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.petsWelcomeStory}</p>
              ) : null
            }
          >
            {data.petsWelcome ? 'Pets are welcome' : 'No pets welcome'}
          </DetailIcon>

          <DetailIcon
            icon="accessibility"
            theme="bg-lime-200"
            iconClass="stroke-lime-800"
            name="accessibility"
            metrics={
              data.accessibilityStory ? (
                <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.accessibilityStory}</p>
              ) : null
            }
          >
            {data.accessibility ? 'Accessibility Friendly.' : 'Not Accessiblity Friendly.'}
          </DetailIcon>

          <DetailIcon
            icon="languages"
            theme="bg-rose-200"
            iconClass="stroke-rose-800"
            name="languages"
            metrics={
              data.landlordLanguagesStory ? (
                <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.landlordLanguagesStory}</p>
              ) : null
            }
          >
            {data.landlordLanguages
              ? 'Landlord supports other languages.'
              : 'Landlord does not support other languages.'}
          </DetailIcon>
        </div>
      </div>

      <div className="space-x-4 flex border-t border-zinc-300  dark:border-zinc-700  pt-6 mt-16 justify-between">
        {canGoBack && (
          <ButtonLink type="button" onClick={handleBack}>
            Back
          </ButtonLink>
        )}
        <div className="flex items-center space-x-4">
          <p className="font-bold text-base">Look good?</p>
          <Button onClick={submitReview}>Post review</Button>
        </div>
      </div>
    </div>
  )
}
